"use client";

import { Dialog, DialogClose, DialogContent, DialogTitle, DialogTrigger } from "@repo/ui";
import { ImageMimeTypes } from "@repo/utils/utils";
import { Download, ExternalLink, FileIcon, Link, X } from "lucide-react";
import Image from "next/image";
import dynamic from "next/dynamic";
import { type ReactNode, useCallback, useRef, useState } from "react";
import { useIs360Image } from "../hooks/use-is-360-image";
import { toast } from "sonner";
import { getPyloMediaById } from "@/services/pylo-media.service";

const PanoViewer = dynamic(() => import("./pano-viewer").then((m) => m.PanoViewer), { ssr: false });

const PDF_MIME_TYPE = "application/pdf";

// Private media is served via short-lived `?jwt=` URLs; public media URLs never expire.
export const isPermanentMediaUrl = (url: string) =>
  /^https?:\/\//.test(url) && !/[?&]jwt=/.test(url);

// The fileservice answers `download=1` with `Content-Disposition: attachment`, so the browser streams the file to disk.
export const toDownloadUrl = (url: string) => `${url}${url.includes("?") ? "&" : "?"}download=1`;

export interface PyloMediaLightboxProps {
  url: string;
  /** Media id — enables refetching a fresh signed URL when the current one expires. */
  mediaId?: string;
  fileName?: string;
  mimeType?: string;
  children?: ReactNode;
  thumbnailSize?: "sm" | "md" | "lg" | "full";
  showPdfPreview?: boolean;
}

const thumbnailSizes = {
  sm: { width: 32, height: 32, className: "h-8 w-8" },
  md: { width: 80, height: 80, className: "h-20 w-20" },
  lg: { width: 128, height: 128, className: "h-32 w-32" },
  full: { width: 200, height: 200, className: "h-full w-full" },
};

export const PyloMediaLightbox = ({
  url,
  mediaId,
  fileName,
  mimeType,
  children,
  thumbnailSize = "md",
  showPdfPreview = false,
}: PyloMediaLightboxProps) => {
  const isImage = ImageMimeTypes.includes(mimeType ?? "");
  const isPdf = mimeType === PDF_MIME_TYPE;
  const size = thumbnailSizes[thumbnailSize];

  // Default thumbnail based on media type. Thumbnails always render the flat
  // image — 360° detection happens only once the lightbox is opened.
  const defaultThumbnail = isImage ? (
    <Image
      src={url}
      alt={fileName ?? "Preview"}
      className={`${size.className} cursor-pointer rounded-lg object-cover transition-opacity hover:opacity-80`}
      width={size.width}
      height={size.height}
    />
  ) : isPdf && showPdfPreview ? (
    <div
      className={`${size.className} cursor-pointer overflow-hidden ${thumbnailSize !== "full" ? "rounded-lg border border-gray-200" : ""}`}
    >
      <iframe
        src={`${url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
        className="pointer-events-none h-full w-[calc(100%+20px)]"
        title={fileName ?? "PDF Preview"}
      />
    </div>
  ) : (
    <div
      className={`${size.className} flex cursor-pointer items-center justify-center rounded-lg bg-gray-100 transition-colors hover:bg-gray-200`}
    >
      <FileIcon className="h-6 w-6 text-gray-500" />
    </div>
  );

  const trigger = children ?? defaultThumbnail;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <div className={thumbnailSize === "full" ? "h-full w-full" : "inline-block"}>{trigger}</div>
      </DialogTrigger>
      <DialogContent
        className="flex h-[calc(100vh-10%)] max-w-[calc(100vw-10%)] flex-col bg-white p-0 md:max-w-[min(calc(100vw-10%),1200px)] [&>button:last-child]:hidden"
        onEscapeKeyDown={(e) => e.stopPropagation()}
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">{fileName ?? "Media preview"}</DialogTitle>
        <LightboxBody
          url={url}
          mediaId={mediaId}
          fileName={fileName}
          isImage={isImage}
          isPdf={isPdf}
        />
      </DialogContent>
    </Dialog>
  );
};

interface LightboxBodyProps {
  url: string;
  mediaId?: string;
  fileName?: string;
  isImage: boolean;
  isPdf: boolean;
}

// Signed URLs are short-lived (~5 min). Cap how many times we refetch a fresh
// one per failure streak to avoid hammering GraphQL on a genuinely broken file.
const MAX_URL_REFRESHES = 2;

/**
 * The opened-lightbox content. Rendered inside `DialogContent`, which Radix
 * only mounts while the dialog is open — so 360° detection runs on open, never
 * for the (potentially many) thumbnails in a table or list.
 */
const LightboxBody = ({ url, mediaId, fileName, isImage, isPdf }: LightboxBodyProps) => {
  // Capture the URL at open time and only change it ourselves (on expiry). We
  // intentionally do NOT resync to the `url` prop — the table's list query
  // background-refetches and mints new signed URLs, which would otherwise force
  // the open viewer to re-detect and re-download on every refetch.
  const [currentUrl, setCurrentUrl] = useState(url);
  const refreshCount = useRef(0);
  const refreshing = useRef(false);

  const { data: is360 } = useIs360Image(currentUrl, isImage);

  // On a load failure, refetch the media by id to mint a fresh signed URL.
  const handleMediaError = useCallback(async () => {
    if (!mediaId || refreshing.current || refreshCount.current >= MAX_URL_REFRESHES) return;
    refreshing.current = true;
    refreshCount.current += 1;
    try {
      const fresh = await getPyloMediaById(mediaId);
      if (fresh?.url) setCurrentUrl(fresh.url);
    } catch {
      // Nothing more we can do — leave the failed state visible.
    } finally {
      refreshing.current = false;
    }
  }, [mediaId]);

  // A successful load means the URL is valid again — allow future refreshes.
  const handleMediaLoad = useCallback(() => {
    refreshCount.current = 0;
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(currentUrl);
    toast.success("Link copied");
  };

  // While detection is pending (is360 is undefined) we show the flat image, so
  // normal images appear instantly and only true panoramas swap to the viewer.
  const content = isImage ? (
    is360 === undefined ? (
      // Detection in flight — show a neutral placeholder rather than eagerly
      // loading the flat image, which for a 360 would transcode the full-size
      // panorama only to be thrown away when we swap to the viewer.
      <div className="flex h-full w-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-[#09DB87]" />
      </div>
    ) : is360 ? (
      <PanoViewer url={currentUrl} onError={handleMediaError} onLoad={handleMediaLoad} />
    ) : (
      <div className="relative h-full w-full">
        <Image
          src={currentUrl}
          alt={fileName ?? "Preview"}
          className="h-full w-full object-contain"
          width={1200}
          height={900}
          onError={handleMediaError}
          onLoad={handleMediaLoad}
        />
      </div>
    )
  ) : isPdf ? (
    <iframe
      src={currentUrl}
      className="h-full w-full rounded-lg"
      title={fileName ?? "PDF Viewer"}
    />
  ) : (
    <div className="flex h-full w-full flex-col items-center justify-center gap-4">
      <FileIcon className="h-16 w-16 text-gray-400" />
      <p className="text-lg font-medium text-gray-700">{fileName}</p>
      <p className="text-sm text-gray-500">Preview not available for this file type</p>
    </div>
  );

  return (
    <>
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
        <div className="flex items-center gap-2">
          {fileName && (
            <span className="max-w-[300px] truncate text-sm font-medium text-gray-700">
              {fileName}
            </span>
          )}
          {is360 && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">
              360°
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <a
            href={currentUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full p-2 transition-colors hover:bg-gray-100"
            title="Open in new tab"
          >
            <ExternalLink size={16} className="text-gray-500" />
          </a>
          {isPermanentMediaUrl(currentUrl) && (
            <button
              type="button"
              className="rounded-full p-2 transition-colors hover:bg-gray-100"
              onClick={handleCopyLink}
              title="Copy link"
            >
              <Link size={16} className="text-gray-500" />
            </button>
          )}
          <a
            href={toDownloadUrl(currentUrl)}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full p-2 transition-colors hover:bg-gray-100"
            title="Download"
          >
            <Download size={16} className="text-gray-500" />
          </a>
          <DialogClose className="rounded-full p-2 transition-colors hover:bg-gray-100">
            <X size={16} className="text-gray-500" />
          </DialogClose>
        </div>
      </div>
      <div className="flex-1 overflow-hidden p-4">{content}</div>
    </>
  );
};

export interface PyloMediaThumbnailProps {
  url: string;
  fileName?: string;
  mimeType?: string;
  size?: "sm" | "md" | "lg";
  showPdfPreview?: boolean;
  className?: string;
}

export const PyloMediaThumbnail = ({
  url,
  fileName,
  mimeType,
  size = "md",
  showPdfPreview = false,
  className,
}: PyloMediaThumbnailProps) => {
  const isImage = ImageMimeTypes.includes(mimeType ?? "");
  const isPdf = mimeType === PDF_MIME_TYPE;
  const dimensions = thumbnailSizes[size];

  if (isImage) {
    return (
      <Image
        src={url}
        alt={fileName ?? "Preview"}
        className={`rounded-lg object-cover ${dimensions.className} ${className ?? ""}`}
        width={dimensions.width}
        height={dimensions.height}
      />
    );
  }

  if (isPdf && showPdfPreview) {
    return (
      <div
        className={`overflow-hidden rounded-lg border border-gray-200 ${dimensions.className} ${className ?? ""}`}
      >
        <iframe
          src={`${url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
          className="pointer-events-none h-full w-[calc(100%+20px)]"
          title={fileName ?? "PDF Preview"}
        />
      </div>
    );
  }

  return (
    <div
      className={`flex items-center justify-center rounded-lg bg-gray-100 ${dimensions.className} ${className ?? ""}`}
    >
      <FileIcon className="h-6 w-6 text-gray-500" />
    </div>
  );
};
